import React, { useEffect, useRef, useState } from 'react';
import './ClientMobile.css';

const pad = (n) => (n < 10 ? '0' : '') + n;
const STEP_ORDER = [
  { key: 'proposal', label: 'Proposal' },
  { key: 'contract', label: 'Contract' },
  { key: 'payment', label: 'Payment' },
  { key: 'planning', label: 'Planning' },
];

export const fmtEventDate = (raw) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw || '');
  if (!m) return raw || '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const to12h = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(t || '');
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
};
export const fmtTimeRange = (start, end) => {
  const s = to12h(start); const e = to12h(end);
  return s && e ? `${s.replace(/ (AM|PM)$/, '')}–${e}` : (s || e || '');
};
export const eventStartMs = (eventDate, startTime) => {
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(eventDate || '');
  if (!d) return 0;
  const t = /^(\d{1,2}):(\d{2})/.exec(startTime || '');
  return new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), t ? Number(t[1]) : 18, t ? Number(t[2]) : 0).getTime();
};
// Skipped modules drop out entirely; the bead numbers, the "N steps away"
// count and the Next label all follow the visible list.
export const buildSteps = (skippedKeys, currentKey) => {
  const visible = STEP_ORDER.filter((s) => !(skippedKeys || []).includes(s.key));
  const idx = visible.findIndex((s) => s.key === currentKey);
  return {
    steps: visible.map((s, i) => ({
      label: s.label,
      state: i < idx ? 'done' : (i === idx ? 'active' : 'todo'),
    })),
    remaining: idx < 0 ? 0 : visible.length - idx - 1,
  };
};

const Check = () => (
  <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
);
const Arrow = () => (
  <svg viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);
const Glass = ({ side }) => (
  <svg className={`glass ${side}`} viewBox="0 0 24 46">
    <path className="wine" d="M9.3 14 L14.7 14 L13.8 20 Q12 22 10.2 20 Z" />
    <path d="M7 5 L17 5 L14.5 20 Q12 22.5 9.5 20 Z" />
    <path d="M12 21 V39" /><path d="M8 40 H16" />
    <circle cx="10.6" cy="10.5" r=".6" /><circle cx="12.4" cy="8.2" r=".5" />
  </svg>
);

const Hero = ({ targetMs }) => {
  const isPast = !targetMs || targetMs <= Date.now();
  const [showCount, setShowCount] = useState(!isPast);
  const dd = useRef(null); const hh = useRef(null); const mm = useRef(null); const ss = useRef(null);
  useEffect(() => {
    if (isPast) return undefined;
    const tick = () => {
      const d = Math.max(0, targetMs - Date.now());
      if (dd.current) dd.current.textContent = Math.floor(d / 86400000);
      if (hh.current) hh.current.textContent = pad(Math.floor((d % 86400000) / 3600000));
      if (mm.current) mm.current.textContent = pad(Math.floor((d % 3600000) / 60000));
      if (ss.current) ss.current.textContent = pad(Math.floor((d % 60000) / 1000));
    };
    tick();
    const t = setInterval(tick, 1000);
    const flip = setInterval(() => setShowCount((s) => !s), 5200);
    return () => { clearInterval(t); clearInterval(flip); };
  }, [isPast, targetMs]);
  const on = !isPast && showCount;
  return (
    <div className="hero">
      {!isPast && (
        <div className={`state ${on ? 'in' : 'out'}`} data-testid="cvm-countdown">
          <div className="cd-row">
            <div className="cd-unit"><div className="cd-num" ref={dd}>00</div><div className="cd-lab">Days</div></div>
            <div className="cd-sep">:</div>
            <div className="cd-unit"><div className="cd-num" ref={hh}>00</div><div className="cd-lab">Hrs</div></div>
            <div className="cd-sep">:</div>
            <div className="cd-unit"><div className="cd-num" ref={mm}>00</div><div className="cd-lab">Min</div></div>
            <div className="cd-sep">:</div>
            <div className="cd-unit"><div className="cd-num" ref={ss}>00</div><div className="cd-lab">Sec</div></div>
          </div>
          <div className="cd-cap"><span className="dot" /> Counting down to your celebration</div>
        </div>
      )}
      <div className={`state ${on ? 'out' : 'in'}`} data-testid="cvm-celebration">
        <div className="cheers">
          <Glass side="gl" />
          <Glass side="gr" />
          <span className="clink">✦</span>
        </div>
        <div className="cel-title">Here&apos;s to a<br />great event!</div>
        <div className="cel-sub">We can&apos;t wait to celebrate with you.</div>
      </div>
    </div>
  );
};

// Header + hero + information-only stepper. No buttons live in here.
export const ClientMobileChrome = ({
  companyName, screenLabel, eventType, eventDate, timeRange, clientName,
  targetMs, steps, lead, nextLabel,
}) => (
  <>
    <div className="eyebrow">
      <span className="co">{companyName}</span> · <span className="dc">{screenLabel}</span>
    </div>
    <div className="meta-line">
      {eventType}{eventType && <i>·</i>}{eventDate}{timeRange && <i>·</i>}{timeRange}
    </div>
    <h1 className="client-name" data-testid="cvm-client-name">{clientName}</h1>
    <Hero targetMs={targetMs} />
    <div className="card" data-testid="cvm-stepper">
      <p className="lead">{lead}</p>
      <div className="steps">
        {steps.map((s) => (
          <div key={s.label} className={s.state === 'active' ? 'step active' : 'step'}>
            <div className={s.state === 'done' ? 'bead done' : 'bead'}>
              {s.state === 'done' ? <Check /> : String(steps.indexOf(s) + 1)}
            </div>
            <div className="step-lab">{s.label}</div>
          </div>
        ))}
      </div>
      <p className="next">Next: <b>{nextLabel}</b></p>
    </div>
  </>
);

export const MobileStickyBar = ({ label, onPrimary, quiet, onQuiet, variant = 'gold', disabled, icon = 'arrow', testid }) => (
  <div className="cvm-bar">
    <div className="bar-in">
      <button
        type="button"
        className={`btn ${variant === 'sign' ? 'btn-sign' : 'btn-gold'}`}
        onClick={onPrimary}
        disabled={disabled}
        data-testid={testid}
      >
        {icon === 'pen' && (
          <svg viewBox="0 0 24 24"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /></svg>
        )}
        {label}
        {icon === 'arrow' && <Arrow />}
      </button>
      <button type="button" className="changes" onClick={onQuiet} data-testid={`${testid}-quiet`}>{quiet}</button>
    </div>
  </div>
);

export const IncludedList = ({ items }) => (
  <div className="sec">
    <p className="sec-lab">What&apos;s included</p>
    <ul className="inc">
      {items.map((t, i) => (
        <li key={t + i}><Check />{t}</li>
      ))}
    </ul>
  </div>
);

export const InvestmentPanel = ({ amount }) => (
  <div className="inv" data-testid="cvm-investment">
    <div className="l">Your investment</div>
    <div className="n">{amount}</div>
    <div className="s">Any enhancements you add are confirmed at review.</div>
  </div>
);

export const ContractSummary = ({ total, retainer, balance, balanceDue }) => (
  <div className="sum" data-testid="cvm-contract-summary">
    <div className="r"><span className="k">Event total</span><span className="v">{total}</span></div>
    <div className="r due"><span className="k">Due now<small>Retainer</small></span><span className="v">{retainer}</span></div>
    <div className="r"><span className="k">Balance{balanceDue ? ` (due ${balanceDue})` : ''}</span><span className="v">{balance}</span></div>
  </div>
);

export const ShortVersion = ({ fullTerms }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="brief">
      <p className="t">The short version</p>
      <ul className="bl">
        <li><Check />We hold your date once the retainer is paid.</li>
        <li><Check />Balance is due two weeks before the event.</li>
        <li><Check />Free to reschedule up to 30 days out.</li>
      </ul>
      <div className={open ? 'acc open' : 'acc'}>
        <div className="acc-h" onClick={() => setOpen((o) => !o)} data-testid="cvm-terms-toggle">
          Read the full terms
          <svg viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" /></svg>
        </div>
        <div className="acc-b"><div className="acc-b-in">{fullTerms}</div></div>
      </div>
    </div>
  );
};

export const UpsellStrip = ({ chips }) => (
  chips.length ? (
    <div className="upsell" data-testid="cvm-upsell">
      <div className="uh"><b>One last touch?</b><span>Optional</span></div>
      <div className="chips">
        {chips.map((c) => (
          <span className="chip" key={c.name}>{c.name} <b>{c.price}</b></span>
        ))}
      </div>
    </div>
  ) : null
);

export const SignaturePanel = ({ value, onChange }) => (
  <div className="sign" data-testid="cvm-signature">
    <div className="l">Type your full legal name</div>
    <input value={value} onChange={(e) => onChange(e.target.value)} autoComplete="off" data-testid="cvm-signature-input" />
    <div className="sigbox"><span data-testid="cvm-signature-preview">{value || ''}</span></div>
    <p className="fine">By signing, this electronic signature is the legal equivalent of a handwritten one under the U.S. E-SIGN Act. A signed copy is emailed to you.</p>
  </div>
);

export const DueCard = ({ amount, label = 'Retainer due now' }) => (
  <div className="due-card" data-testid="cvm-due-card">
    <div className="l">{label}</div>
    <div className="n">{amount}</div>
    <div className="s">This holds your date · balance billed later</div>
  </div>
);

export const PayMethods = ({ zelleAmount, cardAmount, feeAmount, selected, onSelect }) => (
  <div className="sec" style={{ paddingTop: 18 }} data-testid="cvm-pay-methods">
    <p className="sec-lab">How would you like to pay?</p>
    <div className={selected === 'zelle' ? 'pay on' : 'pay'} onClick={() => onSelect('zelle')} data-testid="cvm-pay-zelle">
      <div className="rad" />
      <div className="pm"><div className="t">Zelle</div><div className="d">No processing fee</div></div>
      <div className="amt"><div className="a">{zelleAmount}</div>{feeAmount ? <div className="save">Save {feeAmount}</div> : null}</div>
    </div>
    <div className={selected === 'card' ? 'pay on' : 'pay'} onClick={() => onSelect('card')} data-testid="cvm-pay-card">
      <div className="rad" />
      <div className="pm"><div className="t">Card</div><div className="d">Visa, Mastercard, Amex</div></div>
      <div className="amt"><div className="a">{cardAmount}</div>{feeAmount ? <div className="fee">incl. {feeAmount} fee</div> : null}</div>
    </div>
  </div>
);

export const PaymentSchedule = ({ retainer, balance, balanceDue }) => (
  <div className="schedule" data-testid="cvm-schedule">
    <p className="t">Payment schedule</p>
    <div className="sr now"><span className="d" /><span className="lab"><b>Retainer</b> — today</span><span className="val">{retainer}</span></div>
    {balance ? (
      <div className="sr later"><span className="d" /><span className="lab">Balance{balanceDue ? ` — ${balanceDue}` : ''}</span><span className="val">{balance}</span></div>
    ) : null}
  </div>
);

export const OptionalNote = () => (
  <div className="opt-note" data-testid="cvm-opt-note">
    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>
    <p><b>This part&apos;s optional.</b> Fill in what you know now — you can come back and finish anytime before the event.</p>
  </div>
);

export const PlanningTabs = ({ tabs, active, onSelect }) => (
  <div className="ptabs" data-testid="cvm-planning-tabs">
    {tabs.map((t) => (
      <div key={t} className={t === active ? 'ptab on' : 'ptab'} onClick={() => onSelect(t)} data-testid={`cvm-planning-tab-${t.toLowerCase()}`}>{t}</div>
    ))}
  </div>
);

export default ClientMobileChrome;
